import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, Navigation, Search, X, Loader2 } from 'lucide-react';
import axios from 'axios';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const API_URL = process.env.REACT_APP_API_URL || 'https://bookmyfresh.com/api';
const MAP_CONTAINER_ID = 'google-map-container';

const DEFAULT_CENTER = { lat: 26.9124, lng: 75.7873 };

// ─── SDK Loader ──────────────────────────────────────────────────────────────
let googleMapsLoadPromise = null;
function loadGoogleMapsSDK() {
  if (googleMapsLoadPromise) return googleMapsLoadPromise;
  if (window.google?.maps?.Map) return Promise.resolve();

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const wait = () => {
        if (window.google?.maps?.Map) resolve();
        else setTimeout(wait, 100);
      };
      wait();
    };
    script.onerror = () => reject(new Error('Failed to load Google Maps SDK'));
    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
}

// ─── Backend-proxied reverse geocode ─────────────────────────────────────────
async function reverseGeocode(lat, lng) {
  try {
    const { data } = await axios.get(`${API_URL}/maps/reverse-geocode`, {
      params: { lat, lng },
    });
    if (data?.results?.length > 0) {
      const r = data.results[0];
      return {
        address: r.formatted_address || '',
        area: r.subSubLocality || r.subLocality || r.locality || r.city || '',
        city: r.city || r.district || '',
        state: r.state || '',
        pincode: r.pincode || '',
      };
    }
    return null;
  } catch {
    return null;
  }
}

// ─── Backend-proxied autocomplete ────────────────────────────────────────────
async function searchPlaces(query) {
  if (!query || query.length < 4) return [];
  try {
    const { data } = await axios.get(`${API_URL}/maps/autosuggest`, {
      params: { q: query },
    });
    return (data?.suggestedLocations || []).map((loc) => ({
      name: loc.placeName || loc.placeAddress,
      address: loc.placeAddress || '',
      lat: loc.latitude ? parseFloat(loc.latitude) : null,
      lng: loc.longitude ? parseFloat(loc.longitude) : null,
      placeId: loc.eLoc, // Google place_id stored as eLoc
    }));
  } catch {
    return [];
  }
}

// ─── Backend-proxied place detail (place_id → lat/lng) ───────────────────────
async function resolvePlaceId(placeId) {
  try {
    const { data } = await axios.get(`${API_URL}/maps/place-detail`, {
      params: { placeId },
    });
    if (data?.lat != null && data?.lng != null) {
      return { lat: Number(data.lat), lng: Number(data.lng) };
    }
    return null;
  } catch {
    return null;
  }
}

const GEO_HARD_TIMEOUT_MS = 12000;

function isValidCoord(lat, lng) {
  return typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng);
}

// Normalizes the inconsistent error shapes browsers/our own checks produce into
// {code, message} so callers can branch on `code` reliably (1=denied, 2=unavailable, 3=timeout).
function normalizeGeoError(err) {
  if (err === 'UNSUPPORTED' || err?.message === 'UNSUPPORTED') {
    return { code: 'UNSUPPORTED', message: 'Geolocation is not supported by this browser.' };
  }
  if (err === 'PRECHECK_DENIED' || err?.message === 'PRECHECK_DENIED') {
    return { code: 1, message: 'Location permission is denied.' };
  }
  if (err === 'TIMEOUT' || err?.message === 'TIMEOUT') {
    return { code: 3, message: 'Location request timed out.' };
  }
  switch (err?.code) {
    case 1: return { code: 1, message: 'Location permission denied.' };
    case 2: return { code: 2, message: 'Location currently unavailable.' };
    case 3: return { code: 3, message: 'Location request timed out.' };
    default: return { code: 0, message: err?.message || 'Unable to determine location.' };
  }
}

function requestCurrentPosition(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(normalizeGeoError('UNSUPPORTED'));
      return;
    }

    let settled = false;
    const settle = (fn, arg) => {
      if (settled) return;
      settled = true;
      clearTimeout(hardTimeoutId);
      fn(arg);
    };

    // getCurrentPosition's own `timeout` option doesn't reliably start counting until the
    // permission prompt is answered in every browser — a user who ignores the prompt can
    // leave this pending forever without a hard ceiling here.
    const hardTimeoutId = setTimeout(() => {
      settle(reject, normalizeGeoError('TIMEOUT'));
    }, (options.timeout || GEO_HARD_TIMEOUT_MS) + 2000);

    const fetchPosition = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => settle(resolve, { lat: position.coords.latitude, lng: position.coords.longitude }),
        (err) => settle(reject, normalizeGeoError(err)),
        options,
      );
    };

    // Skip the (potentially slow/hanging) getCurrentPosition call entirely when permission
    // is already known to be denied — avoids an unnecessary delay before showing an error.
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((status) => {
          if (status.state === 'denied') settle(reject, normalizeGeoError('PRECHECK_DENIED'));
          else fetchPosition();
        })
        .catch(fetchPosition);
    } else {
      fetchPosition();
    }
  });
}

// ─── Component ───────────────────────────────────────────────────────────────
const GoogleLocationPicker = ({ onLocationSelect, initialLocation, className = '' }) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [sdkError, setSdkError] = useState('');
  const [addressInfo, setAddressInfo] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchTimeout = useRef(null);
  const onLocationSelectRef = useRef(onLocationSelect);
  onLocationSelectRef.current = onLocationSelect;

  const initialLocationRef = useRef(initialLocation);

  useEffect(() => {
    loadGoogleMapsSDK()
      .then(() => setSdkLoaded(true))
      .catch((err) => setSdkError(err.message));
  }, []);

  const handleLocationUpdate = useCallback(async (lat, lng) => {
    if (!isValidCoord(lat, lng)) return;
    const info = await reverseGeocode(lat, lng);
    setAddressInfo(info);
    onLocationSelectRef.current?.({ lat, lng, addressInfo: info });
  }, []);

  const recenterMap = useCallback((lat, lng, zoom = 17) => {
    if (!isValidCoord(lat, lng)) return;
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return; // map not initialized yet — nothing to move, and reporting the location would be misleading
    try {
      map.setCenter({ lat, lng });
      map.setZoom(zoom);
      marker.setPosition({ lat, lng });
    } catch (err) {
      console.error('Failed to update map position:', err);
      return;
    }
    handleLocationUpdate(lat, lng);
  }, [handleLocationUpdate]);

  // Initialize Google Map
  useEffect(() => {
    if (!sdkLoaded || !window.google?.maps) return;

    const container = document.getElementById(MAP_CONTAINER_ID);
    if (!container) return;

    const rawInitLoc = initialLocationRef.current;
    // Treat an initialLocation with missing/invalid coords the same as "no initialLocation" —
    // falls through to DEFAULT_CENTER and the current-position lookup below.
    const initLoc = isValidCoord(rawInitLoc?.lat, rawInitLoc?.lng) ? rawInitLoc : null;
    const center = initLoc
      ? { lat: initLoc.lat, lng: initLoc.lng }
      : DEFAULT_CENTER;

    let map;
    try {
      map = new window.google.maps.Map(container, {
        center,
        zoom: 14,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
    } catch (err) {
      console.error('Google Maps init failed:', err);
      setSdkError('Map initialization failed — check console for details');
      return;
    }

    mapRef.current = map;

    const marker = new window.google.maps.Marker({
      map,
      position: center,
      draggable: true,
    });
    markerRef.current = marker;

    let cancelled = false;

    marker.addListener('dragend', () => {
      const pos = marker.getPosition();
      if (pos) handleLocationUpdate(pos.lat(), pos.lng());
    });

    map.addListener('click', (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      marker.setPosition({ lat, lng });
      handleLocationUpdate(lat, lng);
    });

    if (initLoc) {
      handleLocationUpdate(center.lat, center.lng);
    } else {
      setIsLocating(true);
      requestCurrentPosition({ enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 })
        .then(({ lat, lng }) => {
          if (cancelled) return;
          recenterMap(lat, lng);
        })
        .catch(() => {
          if (cancelled) return;
          handleLocationUpdate(center.lat, center.lng);
        })
        .finally(() => { if (!cancelled) setIsLocating(false); });
    }

    return () => {
      cancelled = true;
      window.google?.maps?.event?.clearInstanceListeners(marker);
      window.google?.maps?.event?.clearInstanceListeners(map);
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [sdkLoaded, handleLocationUpdate, recenterMap]);

  const handleUseCurrentLocation = async () => {
    setIsLocating(true);
    try {
      const { lat, lng } = await requestCurrentPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
      recenterMap(lat, lng);
    } catch (err) {
      const messages = {
        1: 'Location access denied. Please allow location permission in your browser.',
        2: 'Unable to determine your current location. Try searching for your address instead.',
        3: 'Location request timed out. Try searching for your address instead.',
        UNSUPPORTED: 'Your browser does not support location access. Try searching for your address instead.',
      };
      alert(messages[err?.code] || 'Unable to get your location. Try searching for your address instead.');
    } finally {
      setIsLocating(false);
    }
  };

  const handleSearchInput = (value) => {
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (value.length < 4) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    searchTimeout.current = setTimeout(async () => {
      const results = await searchPlaces(value);
      setSearchResults(results);
      setIsSearching(false);
    }, 400);
  };

  const handleSelectSearchResult = async (place) => {
    let { lat, lng } = place;

    if (!isValidCoord(lat, lng) && place.placeId) {
      const coords = await resolvePlaceId(place.placeId);
      if (coords) { lat = coords.lat; lng = coords.lng; }
    }

    if (mapRef.current && markerRef.current && isValidCoord(lat, lng)) {
      mapRef.current.setCenter({ lat, lng });
      mapRef.current.setZoom(16);
      markerRef.current.setPosition({ lat, lng });
      handleLocationUpdate(lat, lng);
    }
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
  };

  if (sdkError) {
    return (
      <div className={`border border-red-200 rounded-xl p-4 bg-red-50 ${className}`}>
        <p className="text-red-600 text-sm">Failed to load map: {sdkError}</p>
        <p className="text-red-500 text-xs mt-1">Please check your Google Maps API key configuration.</p>
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'your_google_maps_api_key_here') {
    return (
      <div className={`border border-amber-200 rounded-xl p-4 bg-amber-50 ${className}`}>
        <p className="text-amber-700 text-sm font-medium">Map not configured</p>
        <p className="text-amber-600 text-xs mt-1">
          Set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file with a valid Google Maps API key.
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-gray-200 overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-3 bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          disabled={isLocating || !sdkLoaded}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLocating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Navigation className="w-4 h-4 text-blue-500" />
          )}
          {isLocating ? 'Locating...' : 'Use My Location'}
        </button>
        <button
          type="button"
          onClick={() => setShowSearch(!showSearch)}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Search className="w-4 h-4 text-gray-500" />
          Search Location
        </button>
      </div>

      {/* Search Bar */}
      {showSearch && (
        <div className="relative p-3 bg-white border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchInput(e.target.value)}
              placeholder="Type at least 4 characters to search..."
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              autoFocus
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
          {isSearching && (
            <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching...
            </div>
          )}
          {searchResults.length > 0 && (
            <div className="absolute left-3 right-3 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
              {searchResults.map((place) => (
                <button
                  key={place.placeId || `search-${place.lat}-${place.lng}`}
                  type="button"
                  onClick={() => handleSelectSearchResult(place)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                >
                  <p className="text-sm font-medium text-gray-800 truncate">{place.name}</p>
                  <p className="text-xs text-gray-500 truncate">{place.address}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Map Container */}
      <div className="relative">
        {!sdkLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading map...</span>
            </div>
          </div>
        )}
        <div id={MAP_CONTAINER_ID} style={{ width: '100%', height: '300px' }} />
      </div>

      {/* Selected Address Info */}
      {addressInfo && (
        <div className="p-3 bg-green-50 border-t border-green-100">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-green-800 truncate">
                {addressInfo.area || addressInfo.address}
              </p>
              <p className="text-xs text-green-600 truncate">{addressInfo.address}</p>
              {addressInfo.pincode && (
                <p className="text-xs text-green-600 mt-0.5">
                  {addressInfo.city}{addressInfo.state ? `, ${addressInfo.state}` : ''} - {addressInfo.pincode}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <p className="px-3 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-100">
        Click on the map or drag the pin to select a delivery location
      </p>
    </div>
  );
};

export default GoogleLocationPicker;
